import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Truck, DollarSign, ShieldCheck, Package } from 'lucide-react';
import axios from 'axios';
import ProductCard from '@/components/ProductCard';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Animation configurations - defined outside component to prevent re-creation
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const fadeInScale = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.6, delay: 0.2 }
};

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFeaturedProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/products?featured=true`);
      setFeaturedProducts(response.data.slice(0, 6));
    } catch (err) {
      // Featured products are non-critical, show empty section
      setFeaturedProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeaturedProducts();
  }, [fetchFeaturedProducts]);

  const features = useMemo(() => [
    {
      id: 'fast-shipping',
      icon: Truck,
      title: 'Fast Shipping',
      description: 'Quick and reliable delivery to your doorstep'
    },
    {
      id: 'affordable-pricing',
      icon: DollarSign,
      title: 'Affordable Pricing',
      description: 'Competitive prices on all medical supplies'
    },
    {
      id: 'trusted-products',
      icon: ShieldCheck,
      title: 'Trusted Products',
      description: 'Quality medical supplies you can rely on'
    },
    {
      id: 'bulk-ordering',
      icon: Package,
      title: 'Bulk Ordering',
      description: 'Special discounts for bulk purchases'
    }
  ], []);

  return (
    <div>
      <section className="relative bg-white py-16 md:py-24" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={fadeInUp.initial}
              animate={fadeInUp.animate}
              transition={fadeInUp.transition}
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold font-heading tracking-tight text-gray-900 mb-6">
                Quality Medical Supplies You Can Trust
              </h1>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Reliable, affordable, and delivered with care.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/shop" data-testid="shop-now-button">
                  <Button className="bg-medical-primary hover:bg-medical-primary/90 px-8 py-6 text-lg">Shop Now</Button>
                </Link>
                <Link to="/contact" data-testid="contact-us-button">
                  <Button variant="outline" className="border-medical-primary text-medical-primary hover:bg-medical-primary hover:text-white px-8 py-6 text-lg">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={fadeInScale.initial}
              animate={fadeInScale.animate}
              transition={fadeInScale.transition}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1581056771085-3ce30d907416?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Medical supplies"
                className="rounded-2xl shadow-2xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-medical-accent" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold font-heading mb-4">Featured Products</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Discover our most popular medical supplies trusted by healthcare professionals</p>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading products...</div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {featuredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
              <div className="text-center">
                <Link to="/shop" data-testid="view-all-products-link">
                  <Button className="bg-medical-primary hover:bg-medical-primary/90">View All Products</Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold font-heading mb-4">Why Choose Us</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">We're committed to providing the best medical supplies and service</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center p-6 rounded-xl border border-gray-100 hover:shadow-lg transition-shadow"
                  data-testid={`feature-${feature.id}`}
                >
                  <div className="w-16 h-16 bg-medical-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-medical-primary" />
                  </div>
                  <h3 className="font-heading font-medium text-lg mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;